import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, BadgeCheck, CheckCircle2, MapPin } from "lucide-react";
import { statusDotColor } from "@/lib/businessStatus";
import PinButton from "@/components/PinButton";

const ROTATIONS = ["-rotate-3", "-rotate-2", "-rotate-1", "rotate-1", "rotate-2", "rotate-3"];

export default function BusinessCard({ pro, index = 0, variant = "pinned", workedTogether = false }) {
  const navigate = useNavigate();
  const [lifting, setLifting] = useState(false);
  const rotation = ROTATIONS[index % ROTATIONS.length];
  const cardBg = pro.card_color || "#FFFFFF";
  const pinColor = pro.pin_color || "#EF4444";
  const isFeatured = pro.is_pro_plus;

  // Cap visible badges to 2. Priority: Worked Together > Verified.
  const badges = [];
  if (workedTogether) badges.push({ key: "worked", Icon: CheckCircle2, label: "Worked Together", cls: "bg-[#2E6B57] text-[#FFFFFF]" });
  if (pro.verified) badges.push({ key: "verified", Icon: BadgeCheck, label: "Verified Profile", cls: "bg-[#2E6B57]/15 text-[#2C6653]", title: "LIMYA automatically verified this Pro's email and profile completeness. This is not a license verification or endorsement." });
  const visibleBadges = badges.slice(0, 2);

  const handleClick = (e) => {
    e.preventDefault();
    // Respect prefers-reduced-motion → skip animation
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) { navigate(`/pro/${pro.user_id}`); return; }
    setLifting(true);
    setTimeout(() => navigate(`/pro/${pro.user_id}`), 230);
  };

  return (
    <a
      href={`/pro/${pro.user_id}`}
      onClick={handleClick}
      data-testid={`business-card-${pro.user_id}`}
      className={`group relative block paper-card ${variant === "pinned" ? rotation : ""} ${lifting ? "lift-card" : ""} rounded-xl overflow-visible w-full`}
      style={{ background: cardBg }}
    >
      {variant === "pinned" && (
        <div className="push-pin" style={{ "--pin": pinColor }} />
      )}

      {isFeatured && (
        <div className="absolute -top-2 -right-2 z-10 bg-[#222321] text-[#D6B977] border border-[#D6B977] rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide" data-testid={`pro-plus-ribbon-${pro.user_id}`}>
          PRO+
        </div>
      )}

      <div className="p-5 paper-tex">
        {/* Photo + name + category */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative w-14 h-14 rounded-full overflow-hidden ring-2 ring-white shadow-md shrink-0 bg-[#F2F4F2]">
            {pro.avatar_url ? (
              <img src={pro.avatar_url} alt={pro.business_name} className="w-full h-full object-cover" loading="lazy"/>
            ) : (
              <div className="w-full h-full grid place-items-center bg-[#2E6B57] text-[#FFFFFF] font-bold text-xl">
                {pro.business_name?.[0] || "?"}
              </div>
            )}
            {statusDotColor(pro.business_status) && (
              <span
                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white"
                style={{ background: statusDotColor(pro.business_status) }}
                title="Business status"
                data-testid={`business-status-dot-${pro.user_id}`}
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-[#222] text-base leading-tight line-clamp-2" style={{fontFamily:'Outfit'}}>{pro.business_name}</h3>
            <p className="text-xs text-[#222]/60 uppercase tracking-wider font-medium mt-0.5">{pro.category}</p>
            {pro.city && (
              <p className="text-xs text-[#222]/50 mt-0.5 flex items-center gap-1"><MapPin size={11} className="shrink-0"/> {pro.city}</p>
            )}
          </div>
        </div>

        {/* Tagline */}
        {pro.bio && (
          <p className="text-xs text-[#222]/65 leading-snug mb-3 line-clamp-2" data-testid={`card-tagline-${pro.user_id}`}>{pro.bio}</p>
        )}

        {/* Rating */}
        <div className="flex items-center gap-1.5 text-sm mb-3">
          <Star size={14} className="fill-[#E0A83E] text-[#E0A83E]"/>
          <span className="font-bold">{pro.rating?.toFixed(1) || "—"}</span>
          <span className="text-[#222]/50 text-xs">({pro.review_count || 0})</span>
        </div>

        {/* Max 2 badges */}
        {visibleBadges.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {visibleBadges.map(b => (
              <span key={b.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${b.cls}`} data-testid={`badge-${b.key}-${pro.user_id}`} title={b.title || ""}>
                <b.Icon size={10}/> {b.label}
              </span>
            ))}
          </div>
        )}

        {/* Pin without leaving the board */}
        <div onClick={(e) => e.stopPropagation()} className="pt-1">
          <PinButton pro={pro} size="sm" label="Pin" pinnedLabel="Pinned"/>
        </div>
      </div>
    </a>
  );
}
